/*
 * geom.js — pure geometry for the /pdf-editor object layer.
 *
 * Resize, rotate and snap are the operations a user repeats hundreds of times
 * per document, and every one of their failure modes is silent: a box that
 * creeps by a pixel per drag, a corner that is not actually pinned, an image
 * that squashes because the aspect lock rounded the wrong way. None of that
 * throws — the user just ends up with a crooked document.
 *
 * So it lives here, with no DOM and no session, and is locked by
 * tests/js/pdf-editor-geom.test.mjs.
 *
 * All values are page fractions in [0,1], top-left origin — the same
 * convention as the session model and public/js/pdf-coords.js.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.PdfEditorGeom = api;
})(typeof window !== 'undefined' ? window : null, function () {
    'use strict';

    var MIN = 0.005;          // smallest box we allow, as a page fraction

    function norm360(d) { return ((d % 360) + 360) % 360; }
    function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

    /*
     * resize(p, corner, dx, dy, opts) -> placement
     *
     * The corner OPPOSITE the dragged one stays exactly where it was. That is
     * the whole contract; getting it wrong makes a box wander across the page
     * over a series of drags, which reads as "the tool is broken" long before
     * anyone can say why.
     *
     * opts.keepAspect keeps the original ratio (Shift, and the default for
     * images — a squashed signature is a defect the user finds after export).
     */
    function resize(p, corner, dx, dy, opts) {
        opts = opts || {};
        var left = p.nx, top = p.ny, right = p.nx + p.nw, bottom = p.ny + p.nh;

        // Anchor = the corner that must not move.
        var ax = (corner === 'nw' || corner === 'sw') ? right : left;
        var ay = (corner === 'nw' || corner === 'ne') ? bottom : top;

        if (corner === 'nw') { left += dx; top += dy; }
        else if (corner === 'ne') { right += dx; top += dy; }
        else if (corner === 'sw') { left += dx; bottom += dy; }
        else { right += dx; bottom += dy; }

        var w = Math.max(MIN, Math.abs(right - left));
        var h = Math.max(MIN, Math.abs(bottom - top));

        if (opts.keepAspect && p.nw > MIN && p.nh > MIN) {
            var ratio = p.nw / p.nh;
            // Grow to the larger of the two candidate sizes so the box tracks
            // the pointer rather than lagging behind it on the locked axis.
            if (w / h > ratio) h = w / ratio; else w = h * ratio;
        }

        var nx = (corner === 'nw' || corner === 'sw') ? ax - w : ax;
        var ny = (corner === 'nw' || corner === 'ne') ? ay - h : ay;

        return { nx: nx, ny: ny, nw: w, nh: h, rot: p.rot || 0 };
    }

    /*
     * rotateTo(p, pointer, opts) -> placement
     * Angle from the box centre to the pointer, with 0° meaning "upright".
     *
     * opts.aspect — the page's on-screen width / height. Placements are page
     * FRACTIONS, and a fraction across is not a fraction down (A4: 595 vs
     * 842 pt), so the angle has to be taken in real lengths. Until 2026-10-22
     * it was taken in fractions: the box did not follow the pointer — a
     * pointer at 45° turned it to ~35° — and an exact angle could not be hit.
     *
     * Right angles are magnetic (within opts.magnet, default 4°): turning a
     * box to lie along a sideways scan — the case of support chat 708 — is a
     * quarter turn, and a mouse lands on 88° or 91°, which reads as crooked
     * text in a straight table. opts.free (Alt) switches the magnet off.
     * opts.snap (Shift) quantises to 15°; opts.snapStep forces a step for
     * objects that a PDF can turn only by right angles (form fields, links).
     */
    function rotateTo(p, pointer, opts) {
        opts = opts || {};
        var aspect = opts.aspect > 0 ? opts.aspect : 1;
        var cx = p.nx + p.nw / 2, cy = p.ny + p.nh / 2;
        var ang = Math.atan2(pointer.ny - cy, (pointer.nx - cx) * aspect) * 180 / Math.PI + 90;
        var step = opts.snapStep || (opts.snap ? 15 : 0);
        if (step) {
            ang = Math.round(ang / step) * step;
        } else if (!opts.free) {
            var magnet = opts.magnet === undefined ? 4 : opts.magnet;
            var right = Math.round(ang / 90) * 90;
            if (Math.abs(ang - right) <= magnet) ang = right;
        }
        return { nx: p.nx, ny: p.ny, nw: p.nw, nh: p.nh, rot: norm360(ang) };
    }

    /*
     * move(p, dx, dy, opts) -> placement
     * Keeps at least `opts.keepVisible` of the box on the page (default 20%)
     * so an object can never be dragged completely out of reach — recovering
     * one is otherwise impossible without undo.
     */
    function move(p, dx, dy, opts) {
        opts = opts || {};
        var keep = opts.keepVisible === undefined ? 0.2 : opts.keepVisible;
        var minVisibleW = Math.min(p.nw, Math.max(p.nw * keep, 0.02));
        var minVisibleH = Math.min(p.nh, Math.max(p.nh * keep, 0.02));
        return {
            nx: clamp(p.nx + dx, -(p.nw - minVisibleW), 1 - minVisibleW),
            ny: clamp(p.ny + dy, -(p.nh - minVisibleH), 1 - minVisibleH),
            nw: p.nw, nh: p.nh, rot: p.rot || 0,
        };
    }

    /*
     * rotatePlacement(p, deg) -> placement
     *
     * Re-project a placement when the PAGE it sits on is turned by `deg`
     * (a multiple of 90, clockwise).
     *
     * Why this must exist: placements are fractions of the DISPLAYED page, so
     * turning the page changes what those fractions mean. Without this, a
     * highlight drawn over a line of text stays where it was ON SCREEN while
     * the text rotates out from under it — the annotation silently stops
     * marking what it was put there to mark. Nothing errors; the document is
     * just wrong. Caught 2026-08-10 by looking at a screenshot, not by a test.
     *
     * The box's own `rot` advances by the same amount so text placed upright
     * stays upright relative to the content it annotates.
     */
    function rotatePlacement(p, deg) {
        var d = norm360(deg);
        var nx = p.nx, ny = p.ny, nw = p.nw, nh = p.nh;
        var steps = Math.round(d / 90) % 4;
        for (var i = 0; i < steps; i++) {
            // 90° clockwise: a point (x,y) in a w×h box lands at (1-y, x) in
            // the h×w box, and the box's own extent swaps axes.
            var x0 = nx, y0 = ny, w0 = nw, h0 = nh;
            nx = 1 - (y0 + h0);
            ny = x0;
            nw = h0;
            nh = w0;
        }
        return { nx: nx, ny: ny, nw: nw, nh: nh, rot: norm360((p.rot || 0) + d) };
    }

    /* Same re-projection for a freehand stroke's points. */
    function rotatePoints(points, deg) {
        var steps = Math.round(norm360(deg) / 90) % 4;
        var out = (points || []).map(function (pt) { return [pt[0], pt[1]]; });
        for (var i = 0; i < steps; i++) {
            out = out.map(function (pt) { return [1 - pt[1], pt[0]]; });
        }
        return out;
    }

    /*
     * fitInto(imgW, imgH, box) -> {nw, nh}
     * Size a newly dropped image so it fits the target box without distortion.
     */
    function fitInto(imgW, imgH, box) {
        if (!imgW || !imgH) return { nw: box.nw, nh: box.nh };
        var scale = Math.min(box.nw / imgW, box.nh / imgH);
        return { nw: imgW * scale, nh: imgH * scale };
    }

    /*
     * alignGuides(p, others, tol) -> {x, y, guides:[]}
     * Snap the moving box to the edges and centres of its neighbours. This is
     * the difference between a document that looks assembled and one that looks
     * hand-placed, and users cannot achieve it by eye at any zoom.
     */
    function alignGuides(p, others, tol) {
        tol = tol === undefined ? 0.008 : tol;
        var guides = [];
        var out = { nx: p.nx, ny: p.ny };

        var myX = [p.nx, p.nx + p.nw / 2, p.nx + p.nw];
        var myY = [p.ny, p.ny + p.nh / 2, p.ny + p.nh];

        // page centre and edges count as neighbours worth snapping to
        var targetsX = [0, 0.5, 1];
        var targetsY = [0, 0.5, 1];
        (others || []).forEach(function (o) {
            targetsX.push(o.nx, o.nx + o.nw / 2, o.nx + o.nw);
            targetsY.push(o.ny, o.ny + o.nh / 2, o.ny + o.nh);
        });

        var bestX = null;
        myX.forEach(function (mx, i) {
            targetsX.forEach(function (tx) {
                var d = Math.abs(mx - tx);
                if (d <= tol && (!bestX || d < bestX.d)) bestX = { d: d, delta: tx - mx, at: tx, i: i };
            });
        });
        var bestY = null;
        myY.forEach(function (my, i) {
            targetsY.forEach(function (ty) {
                var d = Math.abs(my - ty);
                if (d <= tol && (!bestY || d < bestY.d)) bestY = { d: d, delta: ty - my, at: ty, i: i };
            });
        });

        if (bestX) { out.nx = p.nx + bestX.delta; guides.push({ axis: 'x', at: bestX.at }); }
        if (bestY) { out.ny = p.ny + bestY.delta; guides.push({ axis: 'y', at: bestY.at }); }

        return { nx: out.nx, ny: out.ny, nw: p.nw, nh: p.nh, rot: p.rot || 0, guides: guides };
    }

    /* Bounding box of a freehand stroke, so ink can be selected and moved
       like any other object instead of being an untouchable scribble. */
    function inkBounds(points) {
        if (!points || !points.length) return { nx: 0, ny: 0, nw: 0, nh: 0, rot: 0 };
        var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        points.forEach(function (pt) {
            if (pt[0] < minX) minX = pt[0];
            if (pt[0] > maxX) maxX = pt[0];
            if (pt[1] < minY) minY = pt[1];
            if (pt[1] > maxY) maxY = pt[1];
        });
        return { nx: minX, ny: minY, nw: maxX - minX, nh: maxY - minY, rot: 0 };
    }

    /* Douglas–Peucker. A pointer event stream is ~10x denser than the curve
       needs; every retained point becomes a line segment in the PDF, so an
       unsimplified signature can add tens of KB and visibly slow rendering. */
    function simplify(points, tolerance) {
        var tol = tolerance === undefined ? 0.0015 : tolerance;
        if (!points || points.length < 3) return (points || []).slice();

        function perp(p, a, b) {
            var dx = b[0] - a[0], dy = b[1] - a[1];
            var len = Math.sqrt(dx * dx + dy * dy);
            if (len === 0) return Math.hypot(p[0] - a[0], p[1] - a[1]);
            return Math.abs(dy * p[0] - dx * p[1] + b[0] * a[1] - b[1] * a[0]) / len;
        }
        function run(pts, first, last, keep) {
            var maxD = 0, idx = 0;
            for (var i = first + 1; i < last; i++) {
                var d = perp(pts[i], pts[first], pts[last]);
                if (d > maxD) { maxD = d; idx = i; }
            }
            if (maxD > tol) {
                run(pts, first, idx, keep);
                keep.push(idx);
                run(pts, idx, last, keep);
            }
        }
        var keep = [0];
        run(points, 0, points.length - 1, keep);
        keep.push(points.length - 1);
        keep.sort(function (a, b) { return a - b; });
        return keep.filter(function (v, i, a) { return i === 0 || v !== a[i - 1]; })
                   .map(function (i) { return points[i]; });
    }

    return {
        MIN: MIN,
        resize: resize,
        rotateTo: rotateTo,
        rotatePlacement: rotatePlacement,
        rotatePoints: rotatePoints,
        move: move,
        fitInto: fitInto,
        alignGuides: alignGuides,
        inkBounds: inkBounds,
        simplify: simplify,
        norm360: norm360,
    };
});
